'use client';

import { useId, useRef, useState, useCallback, useEffect, MouseEventHandler } from 'react';
import styles from './navsearch.module.scss';
import { Search } from '@/ui-components/organisms/Search/Search';
import { type EntityListItemProps } from '@/ui-components/molecules/EntityListItem/EntityListItem';
import { SearchResponse } from 'algoliasearch';
import type { EventsItems } from '@algolia/client-insights';
import { insightsClient } from '@algolia/client-insights';
import { SafeHtml } from '@/components/atoms/Display';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { Text } from '@/ui-components/atoms/Text/Text';
import { SearchSuggestions, type SearchChip } from '@/ui-components/molecules/SearchSuggestions/SearchSuggestions';
import { ShowMoreContentCategory } from '@/ui-components/organisms/ShowMoreContentCategory/ShowMoreContentCategory';
import { ItemVariant, LayoutGrid, TileVariant } from '@/ui-components/organisms/ContentCategory/ContentCategory';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { type EntityCategoryItemProps } from '@/ui-components/molecules/EntityCategoryItem/EntityCategoryItem';
import { getCookie } from 'cookies-next';
import { sanitizeQueryParam } from '@/packages/string';

interface Props {
    searchTerm: string;
    open?: boolean;
    minSearchLength?: number;
    maxSearchLength?: number;
    searchSuggestions?: SearchChip[];
    searchSuggestionsLabel?: string;
    searchQuickLinks?: EntityCategoryItemProps[];
    userTypingDelay?: number;
    onCancelClick?: MouseEventHandler;
    quickLinksHeader?: string;
    domain: string;
    algolia: {
        appID: string;
        apiKey: string;
        indexName: string;
    };
}

const NAV_SEARCH_IMAGE_SIZE = 128;

export default function NavSearch({
    searchTerm = '',
    open,
    minSearchLength = 3,
    maxSearchLength = 128,
    searchSuggestions = [],
    searchSuggestionsLabel,
    searchQuickLinks = [],
    userTypingDelay = 1000,
    onCancelClick,
    quickLinksHeader,
    domain,
    algolia,
}: Props) {
    const algoliaInsights = insightsClient(algolia.appID, algolia.apiKey);
    const userToken = getCookie('suserid');
    const minQueryLength = 3;

    const navSearchInputRef = useRef<HTMLInputElement>(null);
    const navSearchRef = useRef<HTMLDivElement>(null);

    const [predictiveResults, setPredictive] = useState<EntityListItemProps[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const navSearchId = useId();

    const hasResults = predictiveResults.length > 0;
    const showSuggestions =
        (searchQuery.trim().length < minSearchLength || isLoading) && !hasResults && searchSuggestions.length > 0;
    const showQuickLinks = !hasResults && searchQuickLinks.length > 0;

    const getPredictive = useCallback(
        async (newQuery: string) => {
            const queryForSearch = newQuery.trim().slice(0, maxSearchLength);
            const queryForDisplay = newQuery.slice(0, maxSearchLength);

            if (queryForSearch.length < minQueryLength) {
                setSearchQuery(queryForDisplay);
                setPredictive([]);
                setIsLoading(false);
                return;
            }

            if (searchTerm === queryForSearch) {
                return;
            }

            const hadResults = predictiveResults.length > 0;
            const oldQueryWasShort = searchQuery.trim().length < minQueryLength;

            if (hadResults || oldQueryWasShort) {
                setIsLoading(true);
            }

            setSearchQuery(queryForDisplay);

            try {
                const searchParams = new URLSearchParams({
                    query: sanitizeQueryParam(queryForSearch),
                    hitsPerPage: '20',
                });

                const response = await fetch('/rest/search?' + searchParams);
                const searchIndex: SearchResponse<AlgoliaResult> = await response.json();

                if(searchIndex.renderingContent?.redirect?.url === 'https://shop.siriusxm.com/') {
                    const customResult:AlgoliaResult = {
                        content: '',
                        depth: 0,
                        description: 'Shop SiriusXM - Save more with exclusive offers on radios for your home and car.',
                        hostname: searchIndex.renderingContent?.redirect?.url,
                        keywords: queryForSearch,
                        objectID: '',
                        part: 0,
                        path: searchIndex.renderingContent?.redirect?.url,
                        title: 'Shop SiriusXM',
                        type: '',
                        url: searchIndex.renderingContent?.redirect?.url,
                        image: '',
                        _highlightResult: {
                            content: '',
                            description: {
                                value: 'Shop SiriusXM - Save more with exclusive offers on radios for your home and car.'
                            },
                            keywords: queryForSearch,
                            title: {
                                value: 'Shop SiriusXM',
                            },
                            url: searchIndex.renderingContent?.redirect?.url,
                        }
                    };
                    searchIndex.hits.unshift(customResult);
                }

                const results: EntityListItemProps[] = searchIndex.hits.map((page, index) => {
                    const algoliaClickEvent: EventsItems = {
                        eventType: 'click',
                        eventName: page.title,
                        index: 'crawler_SXM_Web',
                        userToken: `${userToken}`,
                        timestamp: Date.now(),
                        objectIDs: [page.objectID],
                        positions: [index + 1],
                        queryID: searchIndex.queryID,
                    };

                    const href = encodeURI(page.url || `${domain}/search?q=${page.title}&predictive=true`);

                    return {
                        title: <SafeHtml tag="span" print={page._highlightResult.title.value} />,
                        shortDescription: <SafeHtml tag="span" print={page._highlightResult.description.value} />,
                        image: page.image ? (
                            <ImageWrapper
                                src={page.image}
                                alt=""
                                optimizedHeight={NAV_SEARCH_IMAGE_SIZE}
                                optimizedWidth={NAV_SEARCH_IMAGE_SIZE}
                                optimizedMobileHeight={NAV_SEARCH_IMAGE_SIZE}
                                optimizedMobileWidth={NAV_SEARCH_IMAGE_SIZE}
                            />
                        ) : undefined,
                        href: href,
                        onClick: (e: { preventDefault: () => void }) => {
                            e.preventDefault();

                            algoliaInsights
                                .pushEvents({
                                    events: [algoliaClickEvent],
                                })
                                .then(() => {
                                    window.location.href = href;
                                })
                                .catch(() => {
                                    window.location.href = href;
                                });
                        },
                    };
                });

                setPredictive(results);
            } catch (error) {
                console.error('Error fetching predictive results:', error);
                setPredictive([]);
            } finally {
                if (hadResults || oldQueryWasShort) {
                    setIsLoading(false);
                }
            }
        },
        [domain, searchTerm, predictiveResults, searchQuery, algoliaInsights, userToken, maxSearchLength],
    );

    // Focus on the input field when the search drawer is opened
    useEffect(() => {
        if (open && navSearchInputRef.current) {
            navSearchInputRef.current.focus();
        }
    }, [open]);

    // Clear the search input and results when the search drawer is closed
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            setPredictive([]);
            setIsLoading(false);
        }
    }, [open]);

    const handleSuggestionClick = (suggestion: string) => {
        setSearchQuery(suggestion);
        getPredictive(suggestion);
        if (navSearchInputRef.current) {
            navSearchInputRef.current.focus();
        }
    };

    const contentCategoryProps = {
        items: searchQuickLinks,
        layoutGrid: 'threeInRow' as LayoutGrid,
        itemVariant: 'list' as ItemVariant,
        tileVariant: 'single' as TileVariant,
    };

    return (
        <div className={styles.searchContainer} ref={navSearchRef} id={navSearchId}>
            <Search
                ref={navSearchInputRef}
                value={searchQuery}
                searchResults={predictiveResults}
                onChange={getPredictive}
                isLoading={isLoading}
                minSearchLength={minSearchLength}
                maxSearchLength={maxSearchLength}
                userTypingDelay={userTypingDelay}
                onCancelClick={onCancelClick}
            ></Search>

            {showSuggestions && (
                <Grid container className={styles.suggestionsWrapper}>
                    <Grid col={{ lg: 10 }} start={{ lg: 2 }}>
                        <SearchSuggestions
                            title={searchSuggestionsLabel || ''}
                            searchChips={searchSuggestions}
                            onChipSelect={handleSuggestionClick}
                        />
                    </Grid>
                </Grid>
            )}

            {showQuickLinks && (
                <Grid container className={styles.linksWrapper}>
                    <Grid col={{ lg: 10 }} start={{ lg: 2 }}>
                        {quickLinksHeader && (
                            <Text tag="p" className={styles.searchQuickLinksHeading} variant="h4">
                                {quickLinksHeader}
                            </Text>
                        )}
                        <ShowMoreContentCategory content={contentCategoryProps} />
                    </Grid>
                </Grid>
            )}
        </div>
    );
}
